// Where the call pill sits. Pure helpers: snapping to the nearest screen edge
// and the per-user saved position. The position is stored as an edge plus a
// 0..1 offset along it, so it survives window resizes and different screens.

export const PILL_MARGIN = 12;
// Top centre, just under the header (CU-869fmcc9r): in view, and clear of the
// practice selector, which sits in the header. Nothing is placed above the
// header's bottom edge (`viewport.top`, see headerInset).
export const DEFAULT_POSITION = Object.freeze({ edge: 'top', offset: 0.5 });

const EDGES = ['top', 'bottom', 'left', 'right'];

// The screen below the header (reads the page, not pure): the pill and the sign-in desk prompt
// never cover the app header (practice selector, menus) nor the page's own header row (title,
// date buttons, the dashboard greeting: `data-page-header` on HeaderBreadcrumbs /
// AnalyticsReportsHeader / LaunchpadWelcome), whichever ends lower. A page header taller than
// 45% of the screen, or one scrolled away, doesn't count. Dialog headers never count.
const visibleBottom = (selector) => Array.from(document.querySelectorAll(selector))
    .filter((el) => !el.closest('.MuiDialog-root'))
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.height > 0 && r.bottom > 0)
    .reduce((max, r) => Math.max(max, r.bottom), 0);
// A page header that has scrolled out of view measures as gone, which used to slide the
// pill up onto the page's own tab row part-way through a scroll and back down again
// (CU-869fmc59b). The last good measurement is kept while that header is still mounted,
// i.e. while the same page is open, and forgotten as soon as it is not: a different page
// gets its own measurement, never this one's.
let lastPageBottom = 0;
export const forgetHeaderBottom = () => { lastPageBottom = 0; };
export const headerBottom = () => {
    try {
        const app = visibleBottom('header.MuiAppBar-root');
        const pageRow = visibleBottom('[data-page-header]');
        const measured = pageRow > 0 && pageRow < window.innerHeight * 0.45 ? pageRow : 0;
        if (measured > 0) lastPageBottom = measured;
        else if (document.querySelectorAll('[data-page-header]').length === 0) lastPageBottom = 0;
        const bottom = Math.max(app, measured || lastPageBottom);
        return bottom > 0 ? Math.round(bottom) + 8 : null;
    } catch {
        return null;
    }
};

// What config.js HEADER says the app bar is: 72 px on desktop (lg, 1200 px and up),
// 64 below. The fallback only -- see headerInset.
const CONFIGURED_HEADER = (width) => (width >= 1200 ? 72 : 64);

// Where the header ends, measured from the bar itself rather than assumed, and with
// no gap: the pill sits against the header the way a docked bar does.
//
// It was 72 + 8 on this screen. The bar actually renders 68, so the pill sat 16 px
// below it with page rows showing through the gap, which is what "floating in the
// middle" looks like from the outside even though it was anchored to the top all
// along. Only the app bar is measured. The page's own header row is deliberately
// left out, because following that is what used to drop the pill onto the Call Logs
// tab row part-way through a scroll (CU-869fmc59b).
export const headerInset = (width) => {
    try {
        const bar = visibleBottom('header.MuiAppBar-root');
        if (bar > 0) return Math.round(bar);
    } catch {
        // No DOM (a headless render): fall through to the configured height.
    }
    return CONFIGURED_HEADER(width);
};

const clamp01 = (n) => Math.min(1, Math.max(0, n));

export const isValidPosition = (pos) => Boolean(pos && EDGES.includes(pos.edge) && Number.isFinite(pos.offset));

// Nearest edge to the pill's centre point when the drag ends.
export const snapToEdge = ({ x, y }, { width, height }) => {
    if (![x, y, width, height].every(Number.isFinite)) return DEFAULT_POSITION;
    const distances = { left: x, right: width - x, top: y, bottom: height - y };
    const edge = EDGES.reduce((best, e) => (distances[e] < distances[best] ? e : best), 'top');
    const offset = edge === 'top' || edge === 'bottom' ? x / Math.max(1, width) : y / Math.max(1, height);
    return { edge, offset: clamp01(offset) };
};

// Top-left pixel position for a pill of `size` on a viewport, kept fully on
// screen with PILL_MARGIN around it and below `viewport.top` (the header's
// bottom edge; PILL_MARGIN when not given).
export const pixelPosition = (pos, viewport, size) => {
    const p = isValidPosition(pos) ? pos : DEFAULT_POSITION;
    const minTop = Number.isFinite(viewport.top) ? Math.max(PILL_MARGIN, viewport.top) : PILL_MARGIN;
    const maxLeft = Math.max(PILL_MARGIN, viewport.width - size.width - PILL_MARGIN);
    const maxTop = Math.max(minTop, viewport.height - size.height - PILL_MARGIN);
    const along = (len, total) => Math.round(clamp01(p.offset) * total - len / 2);
    const clampX = (v) => Math.min(maxLeft, Math.max(PILL_MARGIN, v));
    const clampY = (v) => Math.min(maxTop, Math.max(minTop, v));
    switch (p.edge) {
    case 'bottom':
        return { left: clampX(along(size.width, viewport.width)), top: maxTop };
    case 'left':
        return { left: PILL_MARGIN, top: clampY(along(size.height, viewport.height)) };
    case 'right':
        return { left: maxLeft, top: clampY(along(size.height, viewport.height)) };
    case 'top':
    default:
        return { left: clampX(along(size.width, viewport.width)), top: minTop };
    }
};
